import React, { useState, useRef } from 'react';
import { AlertCircle, Moon, User } from 'lucide-react';
import { updateSettings } from '../services/api';
import { useDarkMode } from '../hooks/useDarkMode';
import { useAppContext } from '../contexts/AppContext';
import AvatarCropperModal from '../components/AvatarCropperModal';
import type { OrganizationType } from '../types';

type TitleMode = 'preset-里長' | 'none' | 'custom';

const SettingsPage: React.FC = () => {
    const {
        organizationName, setOrganizationName,
        organizationType, setOrganizationType,
        name, setName,
        title, setTitle,
        globalProfile, setGlobalProfile,
        globalProfileThumbnail,
    } = useAppContext();
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    // 待裁切的檔案；非 null 時顯示裁切彈窗
    const [cropFile, setCropFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { isDark, toggle } = useDarkMode();

    // 將 title 字串映射為 UI 模式
    const initialTitleMode: TitleMode = title === '里長' ? 'preset-里長' : title === null ? 'none' : 'custom';
    const [titleMode, setTitleMode] = useState<TitleMode>(initialTitleMode);
    const [customTitleDraft, setCustomTitleDraft] = useState(title && title !== '里長' ? title : '');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';   // 清空才能重複選同一個檔案
        if (file) setCropFile(file);
    };

    // 裁切完成才成為待儲存的檔案，仍需按「儲存變更」才會上傳
    const handleCropped = (croppedFile: File) => {
        setCropFile(null);
        setPendingFile(croppedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
            if (typeof reader.result === 'string') setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(croppedFile);
    };

    const handleTitleModeChange = (mode: TitleMode) => {
        setTitleMode(mode);
        if (mode === 'preset-里長') setTitle('里長');
        else if (mode === 'none') setTitle(null);
        else setTitle(customTitleDraft.trim() || null);
    };

    const handleCustomTitleChange = (raw: string) => {
        const trimmed = raw.slice(0, 8); // 限制最多 8 字
        setCustomTitleDraft(trimmed);
        if (titleMode === 'custom') setTitle(trimmed.trim() || null);
    };

    const handleSave = async () => {
        const payload = {
            organizationName: organizationName.trim() || null,
            organizationType,
            name: name.trim(),
            title: title && title.trim() !== '' ? title.trim() : null,
        };
        try {
            if (pendingFile) {
                const { updateSettingsWithImage } = await import('../services/api');
                const updated = await updateSettingsWithImage(payload, pendingFile);
                if (updated.globalProfileUrl) setGlobalProfile(updated.globalProfileUrl);
                setPendingFile(null);
            } else {
                await updateSettings(payload);
            }
            alert('設定已儲存');
        } catch (error) {
            console.error('Failed to save settings:', error);
            alert('儲存失敗');
        }
    };

    return (
        <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500 max-w-2xl">
            <header>
                {/* 手機隱藏主標題（已由頂部列顯示） */}
                <h2 className="hidden lg:block text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2">系統設定</h2>
                <p className="text-slate-500 dark:text-slate-400">設定您的基本資料，讓 AI 自動填入文宣預設位置。</p>
            </header>

            {/* 基本資料 */}
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-8 rounded-3xl space-y-6">
                {/* 組織類型 */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">組織類型</label>
                    <div className="flex gap-2">
                        {(['里', '商家'] as OrganizationType[]).map(t => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setOrganizationType(t)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                                    organizationType === t
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : 'bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-400'
                                }`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 組織名稱（可選填） */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">
                        組織名稱 <span className="text-slate-400">（選填）</span>
                    </label>
                    <input
                        type="text"
                        value={organizationName}
                        onChange={(e) => setOrganizationName(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder={organizationType === '里' ? '例如：和平里' : '例如：阿明早餐店'}
                    />
                </div>

                {/* 姓名 */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">姓名</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-3 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                        placeholder="例如：王大明"
                    />
                </div>

                {/* 稱謂 */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-400">稱謂</label>
                    <div className="flex flex-wrap gap-2 items-center">
                        <button
                            type="button"
                            onClick={() => handleTitleModeChange('preset-里長')}
                            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                                titleMode === 'preset-里長'
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-400'
                            }`}
                        >
                            里長
                        </button>
                        <button
                            type="button"
                            onClick={() => handleTitleModeChange('none')}
                            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                                titleMode === 'none'
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-400'
                            }`}
                        >
                            無
                        </button>
                        <button
                            type="button"
                            onClick={() => handleTitleModeChange('custom')}
                            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                                titleMode === 'custom'
                                    ? 'bg-blue-600 border-blue-600 text-white'
                                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-400'
                            }`}
                        >
                            自訂
                        </button>
                        {titleMode === 'custom' && (
                            <input
                                type="text"
                                value={customTitleDraft}
                                onChange={(e) => handleCustomTitleChange(e.target.value)}
                                maxLength={8}
                                className="flex-1 min-w-[120px] bg-slate-50 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder="最多 8 字"
                            />
                        )}
                    </div>
                </div>

                <div className="pt-6 border-t border-slate-200 dark:border-slate-800 space-y-4">
                    <div className="space-y-4">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-400 block">個人形象照 (Global Profile Image)</label>
                        <div className="flex items-center gap-6">
                            <div
                                className="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center cursor-pointer hover:border-blue-500 transition-all flex-shrink-0"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {previewUrl || globalProfile ? (
                                    <img src={previewUrl ?? globalProfileThumbnail ?? globalProfile!} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <User size={36} className="text-slate-400 dark:text-slate-500" />
                                )}
                            </div>
                            <div className="space-y-2">
                                <label className="cursor-pointer px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium transition-colors border border-slate-300 dark:border-slate-700 inline-block">
                                    <span>{pendingFile ? '已選擇：' + pendingFile.name : '選擇圖片...'}</span>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleFileChange}
                                    />
                                </label>
                                <p className="text-xs text-slate-500 dark:text-slate-500">支援 JPG, PNG 格式，點擊頭像或按鈕選擇</p>
                                {pendingFile && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400">圖片尚未上傳，請按「儲存變更」以套用。</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <h4 className="font-bold flex items-center gap-2 text-slate-700 dark:text-slate-300 pt-4">
                        <AlertCircle size={18} className="text-amber-500" /> 資料同步說明
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-500 leading-relaxed">
                        這些設定將會直接影響 AI 生成文案時的稱呼與身份標示。全域形象照一旦上傳後，所有新生成的文宣都會預設帶入該頭像。
                    </p>
                    <button onClick={handleSave} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20">
                        儲存變更
                    </button>
                </div>
            </div>

            {/* 外觀設定 */}
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-8 rounded-3xl">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">外觀設定</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">調整介面的顯示樣式。</p>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Moon size={18} className="text-slate-600 dark:text-slate-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">深色模式</p>
                            <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">
                                開啟後整個介面切換為深色主題，偏好設定會儲存於瀏覽器中。
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={toggle}
                        role="switch"
                        aria-checked={isDark}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                            isDark ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                        }`}
                    >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            isDark ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                    </button>
                </div>
            </div>

            {cropFile && (
                <AvatarCropperModal
                    file={cropFile}
                    onCancel={() => setCropFile(null)}
                    onConfirm={handleCropped}
                />
            )}
        </div>
    );
};

export default SettingsPage;
